import { OnboardingAction, OnboardingView } from '@chiroup/core/types/Onboarding.type';
import {
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  Database,
  ExternalLink,
  Flag,
  ListFilter,
  ReceiptText,
  Settings2,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import OnboardingConversation from './OnboardingConversation';
import { errorMessage, instantLabel, secondaryClass, Section, Status } from './OnboardingUi';

type Props = {
  view: OnboardingView;
  act: (action: OnboardingAction) => Promise<unknown>;
  busy: boolean;
};

const OnboardingChecklist = ({ view, act, busy }: Props) => {
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const visibleTasks = view.tasks.filter(task => {
    const progress = view.taskProgress[task.id];
    return (
      filter === 'all' ||
      (filter === 'flagged'
        ? progress?.flagged
        : filter === 'not_applicable'
          ? progress?.status === 'not_applicable'
          : !progress || progress.status === 'not_started')
    );
  });
  const update = async (action: OnboardingAction) => {
    setError('');
    try {
      await act(action);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  return (
    <Section
      title="Clinic Setup Checklist"
      eyebrow="Shared clinic setup"
      icon={<ClipboardCheck className="h-5 w-5" />}
      description="One shared checklist for clinic Admins. Tasks can be completed in any order; overall progress counts completed applicable categories."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/45 p-1.5">
          {['all', 'outstanding', 'flagged', 'not_applicable'].map(value => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              aria-pressed={filter === value}
              className={
                filter === value
                  ? 'min-h-10 rounded-lg bg-primary-700 px-3 py-2 text-base font-medium text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 dark:bg-primary-400 dark:text-neutral-950 sm:text-sm'
                  : 'min-h-10 rounded-lg px-3 py-2 text-base font-medium text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 dark:focus-visible:outline-primary-400 sm:text-sm'
              }
            >
              {value === 'all'
                ? 'All tasks'
                : value === 'outstanding'
                  ? 'Outstanding'
                  : value === 'flagged'
                    ? 'Flagged'
                    : 'Not Applicable'}
            </button>
          ))}
          {view.checklistOverdue && (
            <div className="rounded-full bg-amber-100 px-3 py-1 text-base font-medium text-amber-900 sm:text-sm">
              Overdue
            </div>
          )}
        </div>
        {error && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
        {!visibleTasks.length && (
          <div
            role="status"
            className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-muted/20 px-5 py-9 text-center"
          >
            <ListFilter
              className="h-7 w-7 text-primary-700 dark:text-primary-300"
              aria-hidden="true"
            />
            <h3 className="font-semibold">No tasks in this view</h3>
            <p className="max-w-[45ch] text-base text-muted-foreground sm:text-sm">
              Choose another filter to review the clinic’s checklist.
            </p>
          </div>
        )}
        {view.categories.map(category => {
          const tasks = visibleTasks.filter(task => task.categoryId === category.id);
          if (!tasks.length) return null;
          const CategoryIcon =
            {
              admin: Settings2,
              schedule: CalendarDays,
              billing: ReceiptText,
              databases: Database,
              'user-settings': UserRound,
            }[category.id] || ClipboardCheck;
          return (
            <details
              key={category.id}
              className="group/category overflow-hidden rounded-xl border border-border bg-background open:border-primary-500/40 open:bg-primary-50/25 dark:open:bg-primary-900/10"
              open={filter !== 'all' || undefined}
            >
              <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-4 p-4 hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-700 dark:focus-visible:outline-primary-400">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/60 text-primary-800 dark:text-primary-300">
                    <CategoryIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <h3 className="font-semibold">{category.title}</h3>
                    <p className="text-base text-muted-foreground sm:text-sm">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="flex max-w-full flex-wrap items-center gap-3 tabular-nums">
                  <div className="space-y-1.5">
                    <p className="text-base text-muted-foreground sm:text-sm">
                      {category.completed}/{category.applicable} tasks · {category.percent}%
                    </p>
                    <progress
                      aria-label={`${category.title} completion`}
                      max={100}
                      value={category.percent}
                      className="h-1.5 w-36 max-w-full overflow-hidden rounded-full bg-muted accent-primary-700 dark:accent-primary-400 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary-700 dark:[&::-webkit-progress-value]:bg-primary-400"
                    />
                  </div>
                  <Status value={category.status} />
                  <ChevronDown
                    aria-hidden="true"
                    className="h-5 w-5 text-muted-foreground motion-safe:transition-transform group-open/category:rotate-180"
                  />
                </div>
              </summary>
              <div className="divide-y divide-border border-t border-border">
                {tasks.map(task => {
                  const progress = view.taskProgress[task.id];
                  return (
                    <div
                      key={task.id}
                      className="space-y-4 p-4 has-checked:bg-primary-50/40 dark:has-checked:bg-primary-900/10 sm:p-5"
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={`onboarding-task-${task.id}`}
                          name={`task-${task.id}`}
                          aria-label={`Mark ${task.title} ${progress?.status === 'done' ? 'Not Started' : 'Done'}`}
                          checked={progress?.status === 'done'}
                          disabled={!view.availability.active || busy}
                          onChange={event =>
                            update({
                              type: 'task',
                              taskId: task.id,
                              status: event.target.checked ? 'done' : 'not_started',
                            })
                          }
                          className="h-6 w-6 shrink-0 rounded-md border-border accent-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 dark:accent-primary-400 dark:focus-visible:outline-primary-400 sm:h-5 sm:w-5"
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <h4 className="font-semibold">
                            <label
                              className="cursor-pointer"
                              htmlFor={`onboarding-task-${task.id}`}
                            >
                              {task.title}
                            </label>
                          </h4>
                          <p className="max-w-[85ch] text-base text-muted-foreground sm:text-sm">
                            {task.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <Status value={progress?.status || 'not_started'} />
                            <p className="text-base text-muted-foreground sm:text-sm">
                              {task.required ? 'Required' : 'Dismissible'}
                            </p>
                            {progress?.flagged && (
                              <Flag
                                className="h-4 w-4 fill-red-500 text-red-500"
                                aria-label="Needs attention"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {task.settingUrl && (
                          <a
                            href={task.settingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={secondaryClass}
                          >
                            Open setting <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          </a>
                        )}
                        {task.resources.map(resource => (
                          <a
                            key={resource.id}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={secondaryClass}
                          >
                            {resource.title}
                            <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          </a>
                        ))}
                        {view.availability.active && (
                          <>
                            {(!progress || progress.status === 'not_started') && (
                              <button
                                type="button"
                                className={secondaryClass}
                                disabled={busy}
                                onClick={() =>
                                  update({
                                    type: 'task',
                                    taskId: task.id,
                                    flagged: !progress?.flagged,
                                  })
                                }
                              >
                                {progress?.flagged ? 'Clear flag' : 'Flag for attention'}
                              </button>
                            )}
                            {!task.required && (
                              <button
                                type="button"
                                className={secondaryClass}
                                disabled={busy}
                                onClick={() =>
                                  update({
                                    type: 'task',
                                    taskId: task.id,
                                    status:
                                      progress?.status === 'not_applicable'
                                        ? 'not_started'
                                        : 'not_applicable',
                                  })
                                }
                              >
                                {progress?.status === 'not_applicable'
                                  ? 'Restore task'
                                  : 'Mark Not Applicable'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      {progress?.changedAt && (
                        <p className="text-base text-muted-foreground sm:text-sm">
                          Last status change: {progress.changedBy?.name || 'Unknown user'} ·{' '}
                          <time dateTime={progress.changedAt}>
                            {instantLabel(progress.changedAt)}
                          </time>
                        </p>
                      )}
                      <OnboardingConversation
                        conversation={progress?.conversation}
                        target="task"
                        targetId={task.id}
                        editable={view.availability.active}
                        busy={busy}
                        act={act}
                      />
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>
    </Section>
  );
};

export default OnboardingChecklist;
